import React, { useState, useRef } from 'react';
import { useInput, FieldTitle, InputProps, useTranslate } from 'react-admin';
import { FormControl, FormHelperText, CircularProgress, Button } from '@mui/material';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firebaseApp } from '../providers/firebase';

// Initialize Firebase Storage
const storage = getStorage(firebaseApp);

interface FirebaseImageInputProps extends InputProps {
  label?: string;
  hidePreview?: boolean; // Optional prop to hide the image preview
  storagePath?: string; // Storage directory path (default: 'categories')
}

const FirebaseImageInput: React.FC<FirebaseImageInputProps> = (props) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useInput(props);
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(value ? value.url : null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper to process and translate the error key.
  const getTranslatedError = (errorValue: any): string => {
    let errorString = '';
    if (errorValue) {
      if (typeof errorValue === 'string') {
        errorString = errorValue;
      } else if (typeof errorValue === 'object' && errorValue.message) {
        errorString = errorValue.message;
      }
      // Remove react-admin wrapper if present. It typically appears as '@@react-admin@@'
      if (errorString.startsWith('@@react-admin@@')) {
        errorString = errorString.replace(/^@@react-admin@@/, '');
        // Remove potential quotes wrapping the key (e.g., "ra.validation.required")
        errorString = errorString.replace(/^"|"$/g, '');
      }
    }
    return translate(errorString);
  };

  // Uploads the file to Firebase Storage under the "categories" path.
  const uploadImageToFirebaseStorage = async (file: File): Promise<any> => {
    try {
      // Combine current timestamp with the file name for uniqueness.
      const storagePath = `${props.storagePath || 'categories'}/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, storagePath);

      // Upload the file.
      const snapshot = await uploadBytes(storageRef, file);

      // Obtain the public URL for the uploaded file.
      const url = await getDownloadURL(snapshot.ref);

      // Build the asset object.
      const asset = {
        type: 'image',
        createdAt: Date.now(),
        width: 0,   // Optionally update with the actual image width.
        height: 0,  // Optionally update with the actual image height.
        url,
      };
      return asset;
    } catch (error) {
      console.error("Firebase upload error:", error);
      throw error;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("[FirebaseImageInput] File input changed", e.target.files);
    if (e.target.files && e.target.files[0]) {
      setLoading(true);
      const file = e.target.files[0];
      console.log(`[FirebaseImageInput] Uploading file: ${file.name} (${file.size} bytes, ${file.type})`);
      try {
        const asset = await uploadImageToFirebaseStorage(file);
        console.log("[FirebaseImageInput] Upload success:", asset);
        setPreviewUrl(asset.url);
        onChange(asset);
      } catch (err) {
        console.error("[FirebaseImageInput] Upload failed:", err);
      } finally {
        setLoading(false);
      }
    } else {
      console.log("[FirebaseImageInput] No file selected");
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <FormControl fullWidth error={!!error} margin="normal">
      <FieldTitle label={props.label || "Image"} source={props.source} />
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        // Custom styled button triggers the hidden file input.
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleButtonClick}
          size="medium"
          style={{ width: '200px' }} // Prevents the button from expanding to full width
        >
          Upload Image
        </Button>
      )}
      {/* Hidden file input */}
      <input 
        ref={fileInputRef}
        type="file" 
        accept="image/*" 
        onChange={handleFileChange} 
        style={{ display: 'none' }} 
      />
      {/* Conditionally show preview based on hidePreview prop */}
      {!props.hidePreview && previewUrl && (
        <img 
          src={previewUrl} 
          alt="preview" 
          style={{
            marginTop: 10,
            maxHeight: 200,
            maxWidth: '100%',
            objectFit: 'contain'
          }}
        />
      )}
      {error && (
        <FormHelperText>
          {getTranslatedError(error)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FirebaseImageInput;